'use client';

import { useId, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import type { Variants } from 'motion/react';
import {
  RiBuilding2Line,
  RiCheckLine,
  RiGiftLine,
  RiLineChartLine,
  RiRocket2Line,
  RiSeedlingLine,
} from '@remixicon/react';
import { useLocale, useTranslations } from 'next-intl';

import { Link } from '@/i18n/navigation';
import { dur, ease, spring, springFade, stagger } from '@/lib/motion';
import { hl } from '@/lib/rich-text';
import { siteLinks } from '@/lib/site-links';
import {
  ANNUAL_DISCOUNT,
  PLANS,
  TRIAL,
  annualMonthlyUsd,
  planAt,
  type Plan,
  type PlanId,
  type Quota,
} from '@/lib/plans';

/**
 * Fiyat — anasayfa özeti. Tam karşılaştırma /pricing'de.
 *
 * ── Bölümün tek zor işi ───────────────────────────────────────────────────
 *
 * Fiyat KOLTUK SAYISINA değil AYLIK REKLAM HARCAMASINA bağlı. Kullanıcı bunu
 * ilk bakışta anlamazsa dört kartı "ucuzdan pahalıya bir liste" sanıp yanlış
 * plan seçer. İlişkiyi üç ayrı katman aynı anda anlatıyor:
 *
 *  1. **Söz.** Başlık ve giriş cümlesi ilişkiyi düz metinle kuruyor; seçilen
 *     kartın "Harcamanıza uygun" rozeti de sonucu kelimeyle söylüyor.
 *     Hareket tek başına bilgi taşımaz (DESIGN.md §1) — o rozet bu kuralın
 *     karşılığı, süs değil. (Ölçeğin altındaki görünür cevap satırı 10 Ağu
 *     2026'da kullanıcı isteğiyle kaldırıldı; yalnızca ekran okuyucuya giden
 *     `role="status"` sürümü kaldı.)
 *  2. **Yapı.** Harcama ölçeği ile kart ızgarası 1200px üstünde AYNI dört
 *     sütunu ve AYNI boşluğu kullanıyor. Sütunu yukarıdan aşağı okuyunca
 *     "bu kadar harcıyorsam bu plan" ilişkisi kendiliğinden çıkıyor. Bu
 *     hizayı bozacak farklı bir `gap` vermeyin.
 *  3. **Hareket.** Ölçek dolduğu anda karşılık gelen kartın çerçevesi
 *     soldan sağa çiziliyor. İki hareket aynı yayla, aynı anda başlıyor —
 *     göz ikisini tek olay sayıyor. Açıkladığı şey tam olarak nedensellik:
 *     harcamayı değiştirdim, plan değişti.
 *
 * ── Kartın anatomisi ─────────────────────────────────────────────────────
 *
 * Sıra: işaret + ad → fiyat → dönem notu → özet cümlesi → ayraç → tikli
 * kota listesi → buton. Seçili kart 2px'lik bir çerçevenin içine giriyor ve
 * çerçevenin tepesindeki şerit rozeti taşıyor.
 *
 * Şerit her kartta AYRILI (`.pricing-card` üst dolgusu), yalnızca seçilide
 * boyanıyor. Yer açıp boş bırakmak yerine şeridi girip çıkarsaydık seçili
 * kart 36px uzayıp kısalırdı; dört kartın gövdesi aynı hatta başlamaz,
 * ızgaranın tamamı her tıklamada zıplardı.
 *
 * ── Neden ölçek bir "filtre" değil ────────────────────────────────────────
 *
 * Seçim hiçbir kartı gizlemiyor, sıralamayı değiştirmiyor. Dördü de her
 * koşulda okunabilir kalıyor: kullanıcı kendi kademesini görmek kadar
 * "bir üstü ne veriyor" sorusunun cevabını da arıyor.
 *
 * ── Rakamlar ─────────────────────────────────────────────────────────────
 *
 * Tek bir sayı bile burada yazılı değil; hepsi `@/lib/plans`'ten geliyor,
 * biçimlendirmesi yerelleştiriliyor ve cümlelere yer tutucu olarak giriyor.
 * Fiyat değişince yalnızca o dosya düzenlenir (§7 uyarısı: rakamlar hipotez).
 * Aynı sebeple schema.org Product/Offer işaretlemesi YOK — ödeme canlı
 * değilken arama motoruna kesinleşmemiş fiyat bildirmek yanlış olur.
 */

/* Ölçekte varsayılan olarak ikinci kademe seçili. Sıfırıncı kademe ölçeği
   neredeyse boş gösteriyor ve "en ucuzu öneriyoruz" gibi okunuyordu; ortadan
   başlamak hem göstergeyi hem de aralığın iki yönünü görünür kılıyor. */
const DEFAULT_TIER = 1;

/* Plan işareti — kartın kimliği, dört kartı yan yana ayırt etmenin en hızlı
   yolu. Kutu İÇİNE ALINMIYOR: ikon doğrudan zemine oturuyor (DESIGN.md §3).
   `typeof RiCheckLine` kullanılıyor çünkü paket `RemixiconComponentType`'ı
   dışa vermiyor — herhangi bir ikon aynı imzayı taşıyor. */
const PLAN_ICON: Record<PlanId, typeof RiCheckLine> = {
  starter: RiSeedlingLine,
  growth: RiLineChartLine,
  pro: RiRocket2Line,
  enterprise: RiBuilding2Line,
};

const sectionV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

/* Başlık bloğu ve ölçek: blur'lu giriş. Bu bölümde blur taşıyan öğe sayısı
   toplam 5 — DESIGN.md §6'daki ~15 sınırının içinde. */
const headV: Variants = {
  hidden: { opacity: 0, y: 10, filter: 'blur(5px)' },
  show: { opacity: 1, y: 0, filter: 'blur(0px)', transition: springFade(spring.default) },
};

const cardsV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight, delayChildren: 0.04 } },
};

/* Kartlarda blur yok: dördü birden blur'lanınca bütçe tek bölümde bitiyor ve
   ağır kartlar "odaklanan" değil "yüklenen" gibi duruyor. Yol da kısa (12px);
   uzun yol bu ağırlıkta yüzeyde savruk görünür. */
const cardV: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

/** Kota → ekranda görünen metin. Sayı yerelleştirilir, kelimeler çevrilir. */
function quotaText(
  value: Quota,
  nf: Intl.NumberFormat,
  words: { unlimited: string; custom: string },
): string {
  if (value === 'unlimited') return words.unlimited;
  if (value === 'custom') return words.custom;
  return nf.format(value);
}

export function Pricing() {
  const t = useTranslations('pricing');
  const locale = useLocale();
  const uid = useId();

  const [tier, setTier] = useState(DEFAULT_TIER);
  const [annual, setAnnual] = useState(false);
  /* Ölçeğin dolu, kart şeridinin açık çizilmesi bu bayrağa bağlı. Eskiden
     bölüm görüş alanına girince `onViewportEnter` ile true oluyordu; sayfanın
     kaydırdıkça açılması 9 Eyl 2026'da kaldırıldığı için baştan true.
     State olarak duruyor: iki hareket variant'a değil değere bağlı. */
  const [entered] = useState(true);

  /* Para birimi HER İKİ dilde de USD. Sayı grubu yerelleştiriliyor
     ($2.500 / $2,500) ama sembol öne sabitleniyor: Intl'in `currency`
     biçimi Türkçede "2.500,00 $" üretiyor ve büyük punto fiyatta sembolün
     sonda kalması "19,00 $" gibi okunup para birimini geri plana atıyor.
     Ayrıca birim satırında ve her plan cümlesinde USD açıkça yazılı. */
  const nf = useMemo(() => new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }), [locale]);
  const nfCents = useMemo(
    () => new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    [locale],
  );
  const nfPercent = useMemo(
    () => new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }),
    [locale],
  );

  /** "$2.500" / "$2,500" — tam sayıysa kuruşsuz, değilse iki hane ($15.20). */
  const usd = (value: number) =>
    `$${Number.isInteger(value) ? nf.format(value) : nfCents.format(value)}`;

  const words = { unlimited: t('value.unlimited'), custom: t('value.custom') };

  /** Bir planın harcama aralığı: "en fazla $10.000" ya da "$50.000 üzeri". */
  const rangeText = (plan: Plan) =>
    plan.spendCapUsd !== null
      ? t('range.upTo', { amount: usd(plan.spendCapUsd) })
      : t('range.over', { amount: usd(plan.spendFloorUsd ?? 0) });

  /**
   * Kart içindeki tikli liste. Kotalar etiket/değer çiftinden CÜMLEYE
   * dönüştü: dar sütunda "Reklam hesabı / 10" iki satıra bölünüp sayıyı
   * etiketinden koparıyordu, "10 bağlı reklam hesabı" tek parça okunuyor.
   *
   * Sayısal olmayan kotalar (`unlimited` / `custom`) aynı cümleye kelime
   * olarak giriyor — "Sınırsız bağlı reklam hesabı". Bu yüzden kota başına
   * ayrı bir çeviri anahtarı yok.
   *
   * İlk satır harcama sınırı ve gövdesi kalın: bölümün ekseni kartın içinde
   * de o. Kalan üçü ona bağlı ayrıntı.
   */
  const featureLines = (plan: Plan): readonly string[] => [
    plan.spendCapUsd !== null
      ? t('feature.spend', { amount: usd(plan.spendCapUsd) })
      : t('feature.spendOver', { amount: usd(plan.spendFloorUsd ?? 0) }),
    t('feature.credits', { value: quotaText(plan.credits, nf, words) }),
    t('feature.accounts', { value: quotaText(plan.accounts, nf, words) }),
    t('feature.members', { value: quotaText(plan.members, nf, words) }),
  ];

  const active = planAt(tier);

  return (
    <section className="pricing" id="pricing" aria-labelledby={`${uid}-title`}>
      {/* Dikey hatlar hero ve SSS ile aynı sütun sınırında; bölüm sayfanın
          ızgarasına oturuyor. Yan sütunlar (grid-item-2fr) basılmıyor —
          çapraz tarama sayfadan kaldırıldı, taşıyacakları bir şey yok. */}
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <motion.div className="pricing-inner" variants={sectionV} initial={false} animate="show">
            <motion.div className="pricing-head" variants={headV}>
              <div className="pricing-head-copy">
                <p className="pricing-eyebrow">{t('eyebrow')}</p>
                <h2 className="pricing-title" id={`${uid}-title`}>
                  {t.rich('title', { hl })}
                </h2>
                <p className="pricing-lead">{t('lead')}</p>
              </div>

              {/* Ödeme dönemi. İki seçenekli bir radyo grubu; segmentin
                  arkasındaki beyaz kapsül `x` ile kayıyor (yüzde değeri
                  kapsülün KENDİ genişliğine göre, yani tam bir segment). */}
              <fieldset className="pricing-billing">
                <legend className="sr-only">{t('billing.legend')}</legend>
                <div className="pricing-billing-switch">
                  <span className="pricing-billing-thumb-layer" aria-hidden="true">
                    <motion.span
                      className="pricing-billing-thumb"
                      initial={false}
                      animate={{ x: annual ? '100%' : '0%' }}
                      transition={spring.snap}
                    />
                  </span>
                  {(['monthly', 'annual'] as const).map((mode) => (
                    <span className="pricing-billing-option" key={mode}>
                      <input
                        className="pricing-billing-input"
                        type="radio"
                        name={`${uid}-billing`}
                        id={`${uid}-billing-${mode}`}
                        checked={annual === (mode === 'annual')}
                        onChange={() => setAnnual(mode === 'annual')}
                      />
                      <label className="pricing-billing-label" htmlFor={`${uid}-billing-${mode}`}>
                        {t(`billing.${mode}`)}
                      </label>
                    </span>
                  ))}
                </div>
              </fieldset>
            </motion.div>

            {/* --- İki iyi haber -------------------------------------------
                Deneme ile yıllık indirimi tek şeritte topluyor: ikisi de
                "başlamadan önce bilmen gereken kolaylık", ayrı ayrı
                durduklarında biri başlığın altında biri anahtarın altında
                kalıp birbirini bulmuyordu.

                Kutu login sayfasının koyu panelindeki .auth-card'ın aynısı —
                keskin köşe, ince kenar, dikey rampa dolgu, dört köşe ölçü
                işareti. Renkleri bu bölümün AÇIK paletine çevrildi: oradaki
                beyaz saydamlıklar gri zeminde tümüyle görünmez kalırdı.

                Şerit ölçeğin ÜSTÜNDE: "önce hiçbir şey ödemiyorsun" bilgisi
                rakamlara ve kademelere bakmadan önce okunmalı. */}
            <motion.div className="pricing-perks" variants={headV}>
              <span className="pricing-perks-mark pricing-perks-mark--tl" aria-hidden="true" />
              <span className="pricing-perks-mark pricing-perks-mark--tr" aria-hidden="true" />
              <span className="pricing-perks-mark pricing-perks-mark--bl" aria-hidden="true" />
              <span className="pricing-perks-mark pricing-perks-mark--br" aria-hidden="true" />

              <p className="pricing-trial">
                <RiGiftLine className="pricing-trial-icon" size={18} aria-hidden="true" />
                <span>{t('trial', { days: TRIAL.days })}</span>
              </p>
              <p className="pricing-billing-save">
                {t('billing.save', { percent: nfPercent.format(ANNUAL_DISCOUNT) })}
              </p>
            </motion.div>

            {/* --- Harcama ölçeği ------------------------------------------
                Dört kademe = dört plan. Segmentler seçili kademeye kadar
                doluyor: gösterge "harcamam bu seviyede" diyor, dolan son
                segmentin altındaki kart da cevabı veriyor.
                Ray parça parça çünkü ızgaranın boşluğunu paylaşıyor — o
                boşluk kartlarla hizayı sağlayan şey, kapatılamaz. */}
            <motion.fieldset className="pricing-scale" variants={headV}>
              <legend className="pricing-scale-legend">{t('scale.legend')}</legend>

              <div className="pricing-scale-grid">
                {PLANS.map((plan, i) => {
                  const isActive = i === tier;
                  const filled = entered && i <= tier;
                  const inputId = `${uid}-tier-${plan.id}`;

                  return (
                    <div
                      className="pricing-scale-cell"
                      key={plan.id}
                      data-active={isActive || undefined}
                    >
                      <span className="pricing-scale-rail" aria-hidden="true">
                        <motion.span
                          className="pricing-scale-fill"
                          initial={{ scaleX: 0 }}
                          animate={{ scaleX: filled ? 1 : 0 }}
                          /* Kademeden uzaklaştıkça küçük bir gecikme: dolum
                             soldan sağa akıyor, dört segment aynı anda
                             zıplamıyor. 3 × 0.05 = 0.15 sn — eşiğin altında. */
                          transition={{ ...spring.default, delay: i * 0.05 }}
                        />
                      </span>

                      {/* Görsel etiket ile erişilebilir ad aynı metin
                          (WCAG 2.5.3); bağlam legend'dan geliyor. */}
                      <input
                        className="pricing-scale-input"
                        type="radio"
                        name={`${uid}-tier`}
                        id={inputId}
                        checked={isActive}
                        onChange={() => setTier(i)}
                      />
                      <label className="pricing-scale-label" htmlFor={inputId}>
                        {rangeText(plan)}
                      </label>
                    </div>
                  );
                })}
              </div>
            </motion.fieldset>

            {/* Görünür cevap satırı kaldırıldı (kullanıcı, 10 Ağu 2026);
                duyuru kaldı. Fare ile kademe değiştirildiğinde ekran okuyucu
                hiçbir şey söylemez — radyo grubu yalnızca KLAVYEYLE gezilince
                kendi seçimini okur, kartın "Harcamanıza uygun" rozeti ise
                sayfada çok aşağıda. `role="status"` o boşluğu kapatıyor. */}
            <p className="sr-only" role="status">
              {t(active.spendCapUsd !== null ? 'answer' : 'answerOver', {
                spend: usd(active.spendCapUsd ?? active.spendFloorUsd ?? 0),
                plan: t(`plans.${active.id}.name`),
              })}
            </p>

            {/* --- Kartlar ------------------------------------------------- */}
            <motion.ul className="pricing-cards" variants={cardsV}>
              {PLANS.map((plan, i) => {
                const isActive = i === tier;
                const monthly = plan.monthlyUsd;
                const shown =
                  monthly === null ? null : annual ? annualMonthlyUsd(monthly) : monthly;
                const priceText = shown === null ? t('custom') : usd(shown);

                /* Yıllık/aylık notu: iki modda da bir şey söylüyor, çünkü
                   boş kalan satır kartlar arasında zıplamaya yol açıyordu.
                   Yükseklik CSS'te sabit; metin değişse de kart oynamıyor. */
                const note =
                  monthly === null
                    ? null
                    : annual
                      ? t('note.annualActive', { price: usd(monthly) })
                      : t('note.annualHint', { price: usd(annualMonthlyUsd(monthly)) });

                const PlanIcon = PLAN_ICON[plan.id];

                return (
                  <motion.li
                    className="pricing-card"
                    key={plan.id}
                    variants={cardV}
                    data-active={isActive || undefined}
                    /* Yüzey rengini plan belirliyor: Enterprise koyu zemin
                       taşıyor (kullanıcı kararı, 10 Ağu 2026). Kural CSS'te
                       `[data-plan='enterprise']` altında toplu duruyor. */
                    data-plan={plan.id}
                  >
                    {/* Çerçeve yüzeyi. Ölçek dolarken bu da soldan sağa
                        çiziliyor; ikisi aynı yay, aynı an. Göz iki hareketi
                        tek olay sayıyor ve nedenselliği kuruyor.
                        Gövde üstünde opak durduğu için görünen tek şey 2px'lik
                        kenar ile tepedeki şerit — yüzeyin kendisi değil. */}
                    <motion.span
                      className="pricing-card-frame"
                      aria-hidden="true"
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: entered && isActive ? 1 : 0 }}
                      transition={spring.default}
                    />

                    {/* Şeridin yüksekliği CSS'te sabit ve her kartta ayrılı;
                        burada yalnızca rozet metni girip çıkıyor. */}
                    <p className="pricing-card-flag">
                      <AnimatePresence initial={false}>
                        {isActive && (
                          <motion.span
                            key="match"
                            className="pricing-card-match"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: dur.fast, ease: ease.out }}
                          >
                            {t('match')}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </p>

                    <div className="pricing-card-body">
                      <div className="pricing-card-id">
                        <PlanIcon className="pricing-card-icon" size={20} aria-hidden="true" />
                        <h3 className="pricing-card-name">{t(`plans.${plan.id}.name`)}</h3>
                      </div>

                      <p className="pricing-price">
                        {/* key = gösterilen metin: dönem değişince rakam
                            yeniden takılıp aşağıdan giriyor, Enterprise'ın
                            "Özel"i değişmediği için hiç kıpırdamıyor. */}
                        <motion.span
                          key={priceText}
                          className={`pricing-amount${shown === null ? ' pricing-amount--word' : ''}`}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={springFade(spring.snap, dur.instant)}
                        >
                          {priceText}
                        </motion.span>
                        {shown !== null && <span className="pricing-unit">{t('unit')}</span>}
                      </p>

                      <p className="pricing-note">{note}</p>

                      {/* Tek başına okunduğunda ne alındığı anlaşılan cümle.
                          Rakamlar yer tutucudan geliyor (bkz. lib/plans.ts). */}
                      <p className="pricing-summary">
                        {plan.monthlyUsd === null
                          ? t(`plans.${plan.id}.summary`, {
                              spend: usd(plan.spendFloorUsd ?? 0),
                            })
                          : t(`plans.${plan.id}.summary`, {
                              price: priceText,
                              spend: usd(plan.spendCapUsd ?? 0),
                              credits: quotaText(plan.credits, nf, words),
                              accounts: quotaText(plan.accounts, nf, words),
                              members: quotaText(plan.members, nf, words),
                            })}
                      </p>

                      {/* Ayraç, liste ve buton tek blok hâlinde kartın DİBİNE
                          yaslanıyor (.pricing-card-foot → margin-top:auto).
                          Böylece dört kartın ayraç çizgisi aynı hatta düşüyor
                          ve kotalar yan yana okunabiliyor. Üstteki özet cümlesi
                          serbest kalıyor — düz yazının satır sayısı zaten
                          karttan karta değişir. */}
                      <div className="pricing-card-foot">
                        <span className="pricing-card-rule" aria-hidden="true" />

                        <ul className="pricing-features">
                          {featureLines(plan).map((line, f) => (
                            <li
                              className={`pricing-feature${f === 0 ? ' pricing-feature--lead' : ''}`}
                              key={line}
                            >
                              <RiCheckLine
                                className="pricing-feature-icon"
                                size={20}
                                aria-hidden="true"
                              />
                              <span>{line}</span>
                            </li>
                          ))}
                        </ul>

                        {/* Adresler lib/site-links.ts'ten — bileşene ham yol
                            yazılmıyor. Ama o dosyanın "live:false ise bağlantı
                            basma" kuralı BURADA uygulanmıyor: /register ve
                            /pricing'e hero ile header zaten gerçek bağlantı
                            veriyor, fiyat kartlarının butonu tıklanamaz düz
                            metne dönerse bölümün işlevi kalmaz. Sayfalar
                            açılınca bayraklar true olacak ve fark kapanacak. */}
                        <Link
                          className="pricing-cta"
                          href={
                            plan.monthlyUsd === null
                              ? siteLinks.contact.href
                              : siteLinks.register.href
                          }
                        >
                          {t(`plans.${plan.id}.cta`)}
                        </Link>
                      </div>
                    </div>
                  </motion.li>
                );
              })}
            </motion.ul>

            {/* Üç not şeridi ve "Tüm limitleri karşılaştırın" bağlantısı
                10 Ağu 2026'da kullanıcı isteğiyle kaldırıldı. Metinleri
                messages/*.json'da `notes.*` ve `compare` altında DURUYOR:
                depo git altında değil, silinseler geri gelmezdi ve tam
                karşılaştırma sayfası (/pricing) açıldığında oraya taşınmaya
                hazır bekliyorlar. */}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
